import { downloadQrCard } from "../lib/qrCardGenerator";
import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Warehouse,
  Plus,
  Search,
  QrCode,
  Eye,
  Pencil,
  Trash2,
  Loader2,
  MapPin,
  AlertCircle,
  RefreshCw,
  HeartPulse,
  FileText
} from "lucide-react";
import { toast } from "sonner";

const Facilities = () => {
  const { user, token } = useAuth();
  const canManageBiomed = user?.role === "admin" || user?.is_biomed_officer === true;
  const [facilities, setFacilities] = useState([]);
  const [filteredFacilities, setFilteredFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [floorFilter, setFloorFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [regeneratingQr, setRegeneratingQr] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    floor: "",
    building: "المبنى الرئيسي",
    facility_type: "عام",
    notes: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchFacilities();
  }, [token]);

  useEffect(() => {
    filterFacilities();
  }, [facilities, searchQuery, floorFilter]);

  const fetchFacilities = async () => {
    try {
      const response = await axios.get(`${API}/facilities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFacilities(response.data);
    } catch (error) {
      toast.error("فشل في تحميل المرافق");
    } finally {
      setLoading(false);
    }
  };

  const filterFacilities = () => {
    let filtered = [...facilities];
    
    if (searchQuery) {
      filtered = filtered.filter(facility =>
        facility.name.includes(searchQuery) ||
        facility.floor.includes(searchQuery) ||
        facility.building.includes(searchQuery)
      );
    }
    
    if (floorFilter !== "all") {
      filtered = filtered.filter(facility => facility.floor === floorFilter);
    }
    
    setFilteredFacilities(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.floor) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }

    setSubmitting(true);
    try {
      if (selectedFacility) {
        await axios.put(`${API}/facilities/${selectedFacility.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("تم تحديث المرفق بنجاح");
      } else {
        await axios.post(`${API}/facilities`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("تم إضافة المرفق بنجاح");
      }
      fetchFacilities();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "حدث خطأ");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (facilityId) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا المرفق؟")) return;
    
    try {
      await axios.delete(`${API}/facilities/${facilityId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("تم حذف المرفق بنجاح");
      fetchFacilities();
    } catch (error) {
      toast.error("فشل في حذف المرفق");
    }
  };

  const resetForm = () => {
    setFormData({ name: "", floor: "", building: "المبنى الرئيسي", facility_type: "عام", notes: "" });
    setSelectedFacility(null);
  };

  const openEditDialog = (facility) => {
    setSelectedFacility(facility);
    setFormData({
      name: facility.name,
      floor: facility.floor,
      building: facility.building,
      facility_type: facility.facility_type || "عام",
      notes: facility.notes
    });
    setDialogOpen(true);
  };

  const openQRDialog = (facility) => {
    setSelectedFacility(facility);
    setQrDialogOpen(true);
  };

  const toggleMedicalReport = async (facility) => {
    try {
      const res = await axios.patch(
        `${API}/facility/${facility.id}/medical-report-toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.show_medical_report ? "تم تفعيل خيار التقرير الطبي على QR" : "تم إلغاء خيار التقرير الطبي");
      fetchFacilities();
    } catch {
      toast.error("فشل في تحديث الخيار");
    }
  };


  const toggleBiomed = async (facility) => {
    try {
      const res = await axios.patch(
        `${API}/facilities/${facility.id}/biomed-toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        res.data.has_biomed_devices
          ? "تم تفعيل إمكانية إضافة أجهزة طبية لهذا المرفق"
          : "تم إلغاء إمكانية إضافة أجهزة طبية لهذا المرفق"
      );
      fetchFacilities();
    } catch {
      toast.error("فشل في تحديث الخيار");
    }
  };

  const handleRegenerateSingleQr = async () => {
    if (!selectedFacility) return;
    setRegeneratingQr(true);
    try {
      const res = await axios.post(
        `${API}/location/facility/${selectedFacility.id}/regenerate-qr?base_url=${encodeURIComponent(window.location.origin)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedFacility({ ...selectedFacility, qr_code: res.data.qr_code });
      toast.success("تم إعادة توليد رمز QR بنجاح");
      fetchFacilities();
    } catch {
      toast.error("فشل إعادة توليد رمز QR");
    } finally {
      setRegeneratingQr(false);
    }
  };

  const floors = [...new Set(facilities.map(f => f.floor))].sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="lucent-loader"><img src="/lucent-logo.png" alt="LUCENT" /><div className="lucent-loader-bar"></div></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="facilities-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#655dc6]">المرافق</h1>
          <p className="text-slate-500 mt-1">إدارة المرافق في المركز ({facilities.length} مرفق)</p>
        </div>
        {user?.role === "admin" && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#655dc6] hover:bg-[#5449a8] text-white" data-testid="add-facility-btn">
                <Plus className="w-4 h-4 ml-2" />
                إضافة مرفق
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" dir="rtl">
              <DialogHeader>
                <DialogTitle className="text-[#655dc6]">
                  {selectedFacility ? "تعديل المرفق" : "إضافة مرفق جديد"}
                </DialogTitle>
                <DialogDescription>
                  أدخل بيانات المرفق
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>اسم المرفق *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="مثال: غرفة الاستراحة"
                    className="text-right"
                    data-testid="facility-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>الطابق *</Label>
                  <Input
                    value={formData.floor}
                    onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                    placeholder="مثال: الطابق 1"
                    className="text-right"
                    data-testid="facility-floor-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>المبنى</Label>
                  <Input
                    value={formData.building}
                    onChange={(e) => setFormData({ ...formData, building: e.target.value })}
                    placeholder="مثال: المبنى الرئيسي"
                    className="text-right"
                    data-testid="facility-building-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>نوع المرفق</Label>
                  <Select value={formData.facility_type} onValueChange={(v) => setFormData({ ...formData, facility_type: v })}>
                    <SelectTrigger data-testid="facility-type-select">
                      <SelectValue placeholder="اختر النوع" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="عام">عام</SelectItem>
                      <SelectItem value="غرفة استراحة">غرفة استراحة</SelectItem>
                      <SelectItem value="مطبخ">مطبخ</SelectItem>
                      <SelectItem value="مخزن">مخزن</SelectItem>
                      <SelectItem value="غرفة كهرباء">غرفة كهرباء</SelectItem>
                      <SelectItem value="غرفة مضخات">غرفة مضخات</SelectItem>
                      <SelectItem value="موقف سيارات">موقف سيارات</SelectItem>
                      <SelectItem value="ممر">ممر</SelectItem>
                      <SelectItem value="سلالم">سلالم</SelectItem>
                      <SelectItem value="مصعد">مصعد</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>ملاحظات</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="ملاحظات إضافية"
                    className="text-right"
                    data-testid="facility-notes-input"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-[#655dc6] hover:bg-[#5449a8] text-white"
                    disabled={submitting}
                    data-testid="submit-facility-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : selectedFacility ? "تحديث" : "إضافة"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                    className="flex-1"
                  >
                    إلغاء
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="البحث في المرافق..."
                className="pr-10 text-right"
                data-testid="search-facilities-input"
              />
            </div>
            <Select value={floorFilter} onValueChange={setFloorFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="floor-filter">
                <SelectValue placeholder="جميع الطوابق" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الطوابق</SelectItem>
                {floors.map(floor => (
                  <SelectItem key={floor} value={floor}>{floor}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Facilities Grid */}
      {filteredFacilities.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredFacilities.map((facility) => (
            <Card
              key={facility.id}
              className="card-hover border-0 shadow-sm group"
              data-testid={`facility-card-${facility.id}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#F5F3F9] flex items-center justify-center group-hover:bg-[#655dc6] transition-colors">
                    <Warehouse className="w-6 h-6 text-[#655dc6] group-hover:text-white transition-colors" />
                  </div>
                  <button
                    onClick={() => openQRDialog(facility)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                    title="عرض رمز QR"
                    data-testid={`qr-btn-${facility.id}`}
                  >
                    <QrCode className="w-5 h-5 text-[#9b8fd4]" />
                  </button>
                </div>
                
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-lg text-[#655dc6]">{facility.name}</h3>
                  {facility.open_requests_count > 0 && (
                    <Badge 
                      className="bg-amber-100 text-amber-700 hover:bg-amber-100 flex items-center gap-1"
                      data-testid={`open-requests-badge-${facility.id}`}
                    >
                      <AlertCircle className="w-3 h-3" />
                      {facility.open_requests_count}
                    </Badge>
                  )}
                </div>
                
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                  <MapPin className="w-4 h-4" />
                  <span>{facility.floor}</span>
                  {facility.building && <span>• {facility.building}</span>}
                </div>
                
                {facility.facility_type && (
                  <Badge variant="outline" className="mb-4">{facility.facility_type}</Badge>
                )}

                <div className="flex gap-2">
                  <Link to={`/facilities/${facility.id}`} className="flex-1">
                    <Button variant="outline" className="w-full" size="sm" data-testid={`view-facility-${facility.id}`}>
                      <Eye className="w-4 h-4 ml-1" />
                      عرض
                    </Button>
                  </Link>
                  {user?.role === "admin" && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleMedicalReport(facility)}
                        title={facility.show_medical_report ? "إلغاء خيار التقرير الطبي" : "تفعيل خيار التقرير الطبي على QR"}
                        className={facility.show_medical_report ? "bg-sky-600 text-white hover:bg-sky-700 border-sky-600" : ""}
                        data-testid={`medical-report-toggle-${facility.id}`}
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                      {canManageBiomed && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleBiomed(facility)}
                          title={facility.has_biomed_devices ? "إلغاء إمكانية إضافة أجهزة طبية" : "تفعيل إمكانية إضافة أجهزة طبية"}
                          className={facility.has_biomed_devices ? "bg-emerald-600 text-white hover:bg-emerald-700 border-emerald-600" : ""}
                          data-testid={`biomed-toggle-${facility.id}`}
                        >
                          <HeartPulse className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(facility)}
                        data-testid={`edit-facility-${facility.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-500 hover:bg-red-50"
                        onClick={() => handleDelete(facility.id)}
                        data-testid={`delete-facility-${facility.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-16 text-center">
            <Warehouse className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-[#655dc6] mb-2">لا توجد مرافق</h3>
            <p className="text-slate-500">
              {searchQuery || floorFilter !== "all" ? "لم يتم العثور على نتائج مطابقة" : "لم يتم إضافة مرافق بعد"}
            </p>
          </CardContent>
        </Card>
      )}

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6]">رمز QR - {selectedFacility?.name}</DialogTitle>
            <DialogDescription>
              امسح هذا الرمز للإبلاغ عن مشكلة صيانة
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-6">
            {selectedFacility?.qr_code && (
              <div className="qr-code-container">
                <img
                  src={`data:image/png;base64,${selectedFacility.qr_code}`}
                  alt={`QR Code for ${selectedFacility.name}`}
                  className="w-48 h-48"
                />
              </div>
            )}
            <p className="mt-4 text-center text-slate-500 text-sm">
              {selectedFacility?.floor} • {selectedFacility?.building}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={async () => {
                await downloadQrCard({
                  qrBase64: selectedFacility.qr_code,
                  name: selectedFacility.name,
                  floor: selectedFacility.floor,
                  building: selectedFacility.building
                });
              }}
              className="flex-1 bg-[#655dc6] hover:bg-[#5449a8] text-white"
            >
              تحميل رمز QR
            </Button>
            <Button
              variant="outline"
              onClick={handleRegenerateSingleQr}
              disabled={regeneratingQr}
              className="flex-1 gap-1"
            >
              {regeneratingQr ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              إعادة توليد
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Facilities;
